import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

export default function Home() {
  const [projects, setProjects] = useState([]);

  const API_BASE = process.env.REACT_APP_API_URL;

  useEffect(() => {
    document.title = "Home | Development Solutions";

    // ✅ Fetch latest 3 projects for Recent Projects section
    axios
      .get(`${API_BASE}/portfolio?limit=3`)
      .then((res) => setProjects(res.data))
      .catch((err) => console.error("Failed to fetch projects", err));
  }, []);

  return (
    <>
      {/* HERO SECTION */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 min-h-[90vh] flex flex-col justify-center">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-10">
            {/* Left Content */}
            <div className="flex-1 text-center lg:text-left">
              <h1
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight"
                style={{
                  color: "#414042",
                  fontFamily: "Montserrat, sans-serif",
                }}
              >
                Empowering Businesses with{" "}
                <span style={{ color: "#1b75bc" }}>
                  Scalable Digital Solutions
                </span>
              </h1>
              <p
                className="mt-4 text-base sm:text-lg max-w-lg mx-auto lg:mx-0"
                style={{
                  color: "#414042",
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 500,
                }}
              >
                We craft innovative web & mobile applications, deliver impactful
                marketing, and provide expert technical consulting to help your
                business thrive in the digital era.
              </p>

              {/* Bullet Points */}
              <ul className="mt-6 space-y-2 text-center lg:text-left">
                {[
                  "✔ High-quality, scalable solutions",
                  "✔ Experienced & certified team",
                  "✔ Agile process with transparent communication",
                ].map((item, idx) => (
                  <li
                    key={idx}
                    className="text-sm sm:text-base"
                    style={{
                      color: "#414042",
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    {item}
                  </li>
                ))}
              </ul>

              {/* CTA Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/contact"
                  className="px-6 py-3 text-white font-bold rounded-md shadow text-center"
                  style={{
                    backgroundColor: "#1b75bc",
                    fontFamily: "Montserrat, sans-serif",
                  }}
                >
                  Get Started
                </Link>
                <Link
                  to="/services"
                  className="px-6 py-3 border rounded-md font-medium text-center"
                  style={{
                    borderColor: "#1b75bc",
                    color: "#1b75bc",
                    fontFamily: "Montserrat, sans-serif",
                  }}
                >
                  Explore Services
                </Link>
              </div>
            </div>

            {/* Hero Image */}
            <div className="flex-1 w-full">
              <img
                src="https://images.pexels.com/photos/3861958/pexels-photo-3861958.jpeg?auto=compress&cs=tinysrgb&w=1600"
                alt="Modern software team"
                className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 min-h-[90vh] flex flex-col justify-center text-center">
          <h2
            className="text-3xl sm:text-4xl font-bold mb-8 lg:mb-12"
            style={{ color: "#414042", fontFamily: "Montserrat, sans-serif" }}
          >
            Why Choose <span style={{ color: "#1b75bc" }}>Dev Sols?</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {[
              {
                title: "Expert Team",
                desc: "Certified developers & consultants with years of experience.",
                img: "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=800",
              },
              {
                title: "Agile Process",
                desc: "We follow agile methodology for faster delivery & adaptability.",
                img: "https://images.pexels.com/photos/3184328/pexels-photo-3184328.jpeg?auto=compress&cs=tinysrgb&w=800",
              },
              {
                title: "Scalable Solutions",
                desc: "Our apps are designed to grow with your business.",
                img: "https://images.pexels.com/photos/3184460/pexels-photo-3184460.jpeg?auto=compress&cs=tinysrgb&w=800",
              },
              {
                title: "Global Clients",
                desc: "Trusted by startups and enterprises across 10+ countries.",
                img: "https://images.pexels.com/photos/3183170/pexels-photo-3183170.jpeg?auto=compress&cs=tinysrgb&w=800",
              },
            ].map((feature, idx) => (
              <div
                key={idx}
                className="bg-white rounded-lg shadow hover:shadow-lg transition overflow-hidden"
              >
                <img
                  src={feature.img}
                  alt={feature.title}
                  className="h-32 sm:h-40 w-full object-cover"
                />
                <div className="p-4 sm:p-6">
                  <h3
                    className="text-lg sm:text-xl font-bold mb-2"
                    style={{
                      color: "#1b75bc",
                      fontFamily: "Montserrat, sans-serif",
                    }}
                  >
                    {feature.title}
                  </h3>
                  <p
                    className="text-sm sm:text-base"
                    style={{
                      color: "#414042",
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    {feature.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 min-h-[90vh] flex flex-col justify-center">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.pexels.com/photos/3184325/pexels-photo-3184325.jpeg?auto=compress&cs=tinysrgb&w=1600"
                alt="About Dev Sols"
                className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
            <div className="order-1 lg:order-2 text-center lg:text-left">
              <h2
                className="text-3xl sm:text-4xl font-bold mb-4"
                style={{
                  color: "#414042",
                  fontFamily: "Montserrat, sans-serif",
                }}
              >
                About <span style={{ color: "#1b75bc" }}>Dev Sols</span>
              </h2>
              <p
                className="text-base sm:text-lg leading-relaxed mb-4"
                style={{
                  color: "#414042",
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 500,
                }}
              >
                Dev Sols is a cutting-edge software house delivering scalable
                web apps, intuitive mobile experiences, and result-driven
                digital marketing strategies. Our team blends innovation with
                technical excellence.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                <div className="text-center lg:text-left">
                  <h4
                    className="text-lg sm:text-xl font-bold"
                    style={{ color: "#1b75bc" }}
                  >
                    Our Mission
                  </h4>
                  <p
                    className="text-sm sm:text-base"
                    style={{ color: "#414042" }}
                  >
                    To empower businesses with digital solutions that drive
                    growth and innovation.
                  </p>
                </div>
                <div className="text-center lg:text-left">
                  <h4
                    className="text-lg sm:text-xl font-bold"
                    style={{ color: "#1b75bc" }}
                  >
                    Our Vision
                  </h4>
                  <p
                    className="text-sm sm:text-base"
                    style={{ color: "#414042" }}
                  >
                    To be the trusted technology partner for companies
                    worldwide.
                  </p>
                </div>
              </div>
              <div className="text-center lg:text-left">
                <Link
                  to="/about"
                  className="mt-6 inline-block px-6 py-3 rounded-md text-white font-bold"
                  style={{ backgroundColor: "#1b75bc" }}
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 min-h-[100vh] flex flex-col justify-center text-center">
          <h2
            className="text-3xl sm:text-4xl font-bold mb-8 lg:mb-12"
            style={{ color: "#414042", fontFamily: "Montserrat, sans-serif" }}
          >
            Our <span style={{ color: "#1b75bc" }}>Services</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {[
              {
                title: "Web Development",
                desc: "High-performance websites & web apps using modern tech.",
                img: "https://images.pexels.com/photos/3861972/pexels-photo-3861972.jpeg?auto=compress&cs=tinysrgb&w=800",
              },
              {
                title: "Mobile Apps",
                desc: "Cross-platform iOS & Android apps with seamless UX.",
                img: "https://images.pexels.com/photos/1181671/pexels-photo-1181671.jpeg?auto=compress&cs=tinysrgb&w=800",
              },
              {
                title: "Digital Marketing",
                desc: "Boost your online presence with SEO & social campaigns.",
                img: "https://images.pexels.com/photos/3184357/pexels-photo-3184357.jpeg?auto=compress&cs=tinysrgb&w=800",
              },
              {
                title: "Tech Consulting",
                desc: "Get expert advice on scaling and optimizing your tech stack.",
                img: "https://images.pexels.com/photos/3184639/pexels-photo-3184639.jpeg?auto=compress&cs=tinysrgb&w=800",
              },
            ].map((service, idx) => (
              <div
                key={idx}
                className="bg-white rounded-lg shadow hover:shadow-lg transition overflow-hidden"
              >
                <img
                  src={service.img}
                  alt={service.title}
                  className="w-full h-36 sm:h-44 object-cover"
                />
                <div className="p-4 sm:p-6">
                  <h3
                    className="text-lg sm:text-xl font-bold mb-2"
                    style={{
                      color: "#1b75bc",
                      fontFamily: "Montserrat, sans-serif",
                    }}
                  >
                    {service.title}
                  </h3>
                  <p
                    className="text-sm sm:text-base"
                    style={{
                      color: "#414042",
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    {service.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex w-full justify-center">
            <Link
              to="/services"
              className="mt-8 lg:mt-10 inline-block px-6 py-3 rounded-md text-white font-bold"
              style={{ backgroundColor: "#1b75bc" }}
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* IMPACT NUMBERS */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 min-h-[90vh] flex flex-col justify-center text-center">
          <h2
            className="text-3xl sm:text-4xl font-bold mb-8 lg:mb-12"
            style={{ color: "#414042", fontFamily: "Montserrat, sans-serif" }}
          >
            Our Impact in <span style={{ color: "#1b75bc" }}>Numbers</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 lg:gap-10">
            {[
              { num: "50+", text: "Projects Delivered" },
              { num: "10+", text: "Happy Clients" },
              { num: "5+", text: "Countries Served" },
            ].map((stat, idx) => (
              <div key={idx}>
                <h3
                  className="text-4xl sm:text-5xl font-bold"
                  style={{
                    color: "#1b75bc",
                    fontFamily: "Montserrat, sans-serif",
                  }}
                >
                  {stat.num}
                </h3>
                <p
                  className="mt-2 text-base sm:text-lg"
                  style={{
                    color: "#414042",
                    fontFamily: "Montserrat, sans-serif",
                  }}
                >
                  {stat.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ✅ RECENT PROJECTS (Dynamic from API) */}
      <section className="bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 min-h-[100vh] flex flex-col justify-center text-center">
          <h2
            className="text-3xl sm:text-4xl font-bold mb-8 lg:mb-12"
            style={{ color: "#414042", fontFamily: "Montserrat, sans-serif" }}
          >
            Recent <span style={{ color: "#1b75bc" }}>Projects</span>
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {projects.length > 0 ? (
              projects.map((proj) => (
                <div
                  key={proj.id}
                  className="rounded-lg overflow-hidden shadow hover:shadow-lg transition bg-white"
                >
                  {/* Project Image */}
                  <img
                    src={`${API_BASE.replace("/api", "")}/${proj.image}`}
                    alt={proj.title}
                    className="w-full h-40 sm:h-48 object-cover"
                  />

                  {/* Project Info */}
                  <div className="p-4 text-left">
                    {/* ✅ Category */}
                    <span className="inline-block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                      {proj.category}
                    </span>

                    {/* ✅ Title */}
                    <h3
                      className="text-base sm:text-lg font-bold mb-2"
                      style={{
                        color: "#1b75bc",
                        fontFamily: "Montserrat, sans-serif",
                      }}
                    >
                      {proj.title}
                    </h3>

                    {/* ✅ Tech Stack as Tiles */}
                    {proj.tech_stack && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {proj.tech_stack.split(",").map((tech, idx) => (
                          <span
                            key={idx}
                            className="px-2 sm:px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full shadow-sm"
                          >
                            {tech.trim()}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 col-span-full">
                No Latest Projects Found
              </p>
            )}
          </div>
          <div className="flex w-full justify-center">
            <Link
              to="/portfolio"
              className="mt-8 lg:mt-10 inline-block px-6 py-3 rounded-md text-white font-bold"
              style={{ backgroundColor: "#1b75bc" }}
            >
              View Full Portfolio
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 min-h-[100vh] flex flex-col justify-center text-center">
          <h2
            className="text-3xl sm:text-4xl font-bold mb-8 lg:mb-12"
            style={{ color: "#414042", fontFamily: "Montserrat, sans-serif" }}
          >
            What Our <span style={{ color: "#1b75bc" }}>Clients Say</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                name: "Richard Latham",
                role: "Director of eCommerce at MEI Corporation",
                text: "Devsols is a true professional software company that values punctuality and integrity. Their team is highly skilled in PHP, Laravel, and modern web technologies, making them a vital asset to any project. They are well-versed in agile methodologies and are excellent problem solvers. I give them my full recommendation.",
                img: "/testimonials/richard.jpg",
              },
              {
                name: "Abdullah Gillani",
                role: "CTO at Shawarmer",
                text: "I have always found Devsols to have great potential in learning, understanding, and implementing new technologies. They consistently deliver quality solutions and are a reliable, collaborative team to work with.",
                img: "/testimonials/abdullah.jpg",
              },
              {
                name: "Ehtesham Rasool",
                role: "Marketing Manager",
                text: "Hiring Devsols for website development was a great experience. They built two professional websites—an E-Commerce store and a corporate site—using PHP, MySQL, HTML, CSS, Bootstrap, and JavaScript. Their expertise in both front-end and back-end development, combined with their ability to understand client needs, is truly impressive. Highly recommended!",
                img: "/testimonials/default.jpg",
              },
            ].map((t, idx) => (
              <div
                key={idx}
                className="bg-gray-50 p-4 sm:p-6 rounded-lg shadow hover:shadow-lg transition"
              >
                <img
                  src={t.img}
                  alt={t.name}
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-full mx-auto mb-4 object-cover"
                />
                <p
                  className="italic mb-4 text-sm sm:text-base"
                  style={{
                    color: "#414042",
                    fontFamily: "Montserrat, sans-serif",
                  }}
                >
                  "{t.text}"
                </p>
                <h4
                  className="font-bold text-sm sm:text-base"
                  style={{ color: "#1b75bc" }}
                >
                  {t.name}
                </h4>
                <p
                  className="text-xs sm:text-sm"
                  style={{
                    color: "#414042",
                    fontFamily: "Montserrat, sans-serif",
                  }}
                >
                  {t.role}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 min-h-[100vh] flex flex-col justify-center text-center">
          <h2
            className="text-3xl sm:text-4xl font-bold"
            style={{ color: "#414042", fontFamily: "Montserrat, sans-serif" }}
          >
            Ready to Transform Your Business?
          </h2>
          <p
            className="mt-4 text-base sm:text-lg max-w-2xl mx-auto"
            style={{
              color: "#414042",
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 500,
            }}
          >
            Let's build innovative, scalable digital solutions together. We're
            here to turn your ideas into reality.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-block px-6 sm:px-8 py-3 sm:py-4 rounded-md text-white font-bold"
            style={{ backgroundColor: "#1b75bc" }}
          >
            Contact Us Today
          </Link>
        </div>
      </section>
    </>
  );
}
